import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useSearchParams } from "react-router-dom";
import { unzip } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { omit } from "lodash";
import { ActiveProjectStore, ActiveProjectStoreProvider } from "@stores";
import {
  getActiveProjectsQuery,
  getCompanySettingsQuery,
  initDemoProjectQuery,
  setCompanySettingsQuery,
  getAnnouncementsQuery,
  setAnnouncementReadStatusQuery,
  getCompanyRateCardQuery,
  getSharedProjectsQuery,
} from "@query";
import { ActiveProjectsSocketProvider, COMPANY_SETTINGS } from "@client";
import { useStores } from "@hooks";
import { DASHBOARD_MODE, PROJECT_COLUMN_COLORS, PROJECT_COLUMNS } from "@utils";
import { JiraBTSetupDialog, JiraExportAndBTInfoDialog, JiraExportModal, Announcements } from "@dialogs";
import { PageCircularProgress, UploadSnackBar } from "@components";
import { ActiveProjectsView } from "./ActiveProjectsView";

export const ActiveProjects = observer(() => {
  const { t } = useTranslation();
  const { userStore, stateStore } = useStores();
  const { enqueueSnackbar } = useSnackbar();
  
  const [ isLoading, setLoading ] = useState(false);
  const [ columns, setColumns ] = useState();
  const [ btQuery, setBTQuery ] = useState();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ announcements, setAnnouncements ] = useState();
  const [ jiraExportId, setJiraExportId ] = useState();
  const [ dashboardMode, setDashboardMode ] = useState(DASHBOARD_MODE.COMPANY);
  const [ openBTSetupDialog, setOpenBTSetupDialog ] = useState(false);
  const [ openJiraExportDialog, setOpenJiraExportDialog ] = useState(false);

  const [ activeProjectStore ] = useState(new ActiveProjectStore());
  
  useEffect(() => {
    if (columns)
      loadProjects();
  }, [dashboardMode]);

  useEffect(() => {
    const btCb = searchParams.get("btCb");
    const btSu = searchParams.get("btSu");
    if (btCb) {
      setBTQuery(JSON.parse(unzip(btCb)));
      setOpenJiraExportDialog(Boolean(btCb));
    }
    if (btSu) {
      setBTQuery(JSON.parse(unzip(btSu)));
      setOpenBTSetupDialog(Boolean(btSu));
    }
    if(btCb || btSu)
      setSearchParams();
    
    loadData();
  }, []);
  
  const checkAnnouncements = async () => {
    const an = await getAnnouncementsQuery(userStore.userUuid);
    setAnnouncements(an);
  }

  const handleOpenInfo = (jiraExportId) => {
    setJiraExportId(jiraExportId);
  };

  const handleCloseInfo = () => {
    enqueueSnackbar(
      <UploadSnackBar jiraExportId={jiraExportId} />,
      {
        variant: "info",
        key: jiraExportId,
        persist: true,
        preventDuplicate: true,
      }
    );
    setJiraExportId(undefined);
  };

  const loadData = async () => {
    
    await checkAnnouncements();
    
    if (stateStore.firstLogin) {
      await initDemoProjectQuery();
    }
    
    const workTypes = await getCompanyRateCardQuery();
    activeProjectStore.setWorkTypes(workTypes);

    loadProjects();
    
    const namedCols = await getCompanySettingsQuery(
      COMPANY_SETTINGS.HOME_COLUMNS
    );

    const cols = Object.keys(PROJECT_COLUMNS)
      .reduce((o, col) => [
        ...o,
        {
          code: PROJECT_COLUMNS[col],
          color: PROJECT_COLUMN_COLORS[col],
          name: namedCols?.[PROJECT_COLUMNS[col]]
        },
      ], []);

    setColumns(cols);
    /**
     * @note: 10.10.24 - temporary removed this BT-Api call:
     
     try {
       const j = await getCompanyProjectsStatusQuery(userStore.data.companyId);
       activeProjectStore.setBtProjectsStatus(j.projects);
     } catch (e) {
       console.error("Could not fetch BT projects");
     }
     */
  };
  
  const loadProjects = async () => {
    setLoading(true);
    const p = dashboardMode === "shared"
      ? await getSharedProjectsQuery()
      : await getActiveProjectsQuery(userStore.workspaceUuid);
    activeProjectStore.setProjects(p);
    setLoading(false);
  }
  
  const handleAnnouncementModalClose = () => {
    shownAnnouncements.announcements.forEach(a => {
      setAnnouncementReadStatusQuery(a.id);
    })
    const u = omit(announcements, shownAnnouncements.type);
    setAnnouncements(Object.keys(u).length ? u : undefined)
  }
  
  const handleColumnNameChange = useCallback(async (code, newName) => {
    const c = [...columns];
    const column = c.find((col) => col.code === code);
    if (!column) return;

    column.name = newName;

    await setCompanySettingsQuery(
      COMPANY_SETTINGS.HOME_COLUMNS,
      c.reduce((o, { code, name }) => ({ ...o, [code]: name }), {})
    );
    setColumns(c);
  }, [columns]);
  
  const handleDashboardModeChange = (v) => {
    setDashboardMode(v)
  }

  const shownAnnouncements = useMemo(() => {
    if(!announcements) return;
    const keys = Object.keys(announcements);
    if(!keys.length) return;
    return {
      announcements: announcements[keys[0]],
      type: keys[0]
    };
  }, [announcements]);
  
  const jiraRedirectProjectId = useMemo(() => btQuery?.projectId, [btQuery]);
  
  const jiraRedirectProjectName = useMemo(() => btQuery?.jiraProjectName, [btQuery]);
  
  if(!userStore.workspaceUuid || !columns || isLoading)
    return <PageCircularProgress />

  return (
    <ActiveProjectStoreProvider value={activeProjectStore}>
      <ActiveProjectsSocketProvider>
        <ActiveProjectsView
          columns={columns}
          handleColumnNameChange={handleColumnNameChange}
          dashboardMode={dashboardMode}
          onDashboardModeChange={handleDashboardModeChange}
        />
      </ActiveProjectsSocketProvider>
      {
        shownAnnouncements &&
        <Announcements
          updates={shownAnnouncements.announcements}
          variant={shownAnnouncements.type}
          onClose={handleAnnouncementModalClose}
        />
      }
      {
        openJiraExportDialog &&
        <JiraExportModal
          onClose={() => setOpenJiraExportDialog(false)}
          uuid={jiraRedirectProjectId}
          projectJiraName={jiraRedirectProjectName}
          onOpenInfo={handleOpenInfo}
        />
      }
      {
        openBTSetupDialog &&
        <JiraBTSetupDialog
          onClose={() => setOpenBTSetupDialog(false)}
          uuid={jiraRedirectProjectId}
          isOpen={openBTSetupDialog}
        />
      }
      {
        jiraExportId &&
        <JiraExportAndBTInfoDialog
          hideNotClose
          openModal
          title={t("views.bt.info_create_project.title")}
          
          onClose={handleCloseInfo}
          width={600}
          actions={<UploadSnackBar jiraExportId={jiraExportId} />}
        >
          <span>{t("views.bt.info_create_project.text1")}</span>
          <br />
          <span>{t("views.bt.info_create_project.text2")}</span>
        </JiraExportAndBTInfoDialog>
      }
    </ActiveProjectStoreProvider>
  )
});
